import {type KeyboardEvent} from 'react';
import classnames from 'classnames';
import noop from '@tinkoff/utils/function/noop';

import type {FileInfo} from '../../../utils/attachments';
import {ImagePlaceholder, useImageByRef} from '../../../atoms/image';

import {CopyAction} from './copy-action';
import {DownloadAction} from './download-action';
import styles from './embedded-image.module.css';

type Props = {
    fileInfo: FileInfo;
    getFileUrl: (id: string) => string;
    getFilePreviewUrl: (id: string) => string;
    getFilePublicUrl?: (id: string) => Promise<string>;
    enableDownload?: boolean;
    enableCopy?: boolean;
    className?: string;
    onClick?: () => void;
    onDownload?: () => void;
    onCopy?: (url: string) => void;
};

const MIN_IMAGE_SIZE = 48;

export const EmbeddedImage = ({
    fileInfo,
    getFileUrl,
    getFilePreviewUrl,
    getFilePublicUrl,
    onClick = noop,
    enableDownload,
    enableCopy,
    className,
    onCopy,
    onDownload,
}: Props) => {
    const {has_preview_image: hasPreviewImage, id} = fileInfo;
    const fileUrl = getFileUrl(id);
    const previewUrl = hasPreviewImage ? getFilePreviewUrl(id) : fileUrl;
    const {isLoaded, setImageRef} = useImageByRef();

    const areActionsEnabled = enableCopy || enableDownload;

    const handleKeyDown = (e: KeyboardEvent<HTMLImageElement>) => {
        if (e.key === 'Enter') {
            onClick();
        }
    };

    const image = (
        <img
            ref={setImageRef}
            className={classnames(styles.image, {[styles.imageLoading]: !isLoaded})}
            alt={fileInfo.name}
            src={previewUrl}
            onKeyDown={handleKeyDown}
            onClick={onClick}
            decoding='async'
            loading='lazy'
        />
    );

    const actions = (
        <div className={styles.actions} data-testid='actions'>
            {enableCopy && <CopyAction onCopy={onCopy} getFilePublicUrl={getFilePublicUrl} fileId={id} />}
            {enableDownload && <DownloadAction fileUrl={fileUrl} onClick={onDownload} />}
        </div>
    );

    return (
        <figure
            className={classnames(className, styles.imageContainer, {
                [styles.smallImage]: isSmallImage(fileInfo),
                [styles.placeholderContainer]: !isLoaded,
            })}
        >
            {!isLoaded && <ImagePlaceholder width={fileInfo.width} height={fileInfo.height} />}
            {image}
            {isLoaded && areActionsEnabled && actions}
        </figure>
    );
};

function isSmallImage(fileInfo: FileInfo) {
    return fileInfo.width < MIN_IMAGE_SIZE || fileInfo.height < MIN_IMAGE_SIZE;
}
